<template>
	<div class="flex-container">
		<div class="tab-view" :class="tabViewWidthClass">
			<Tabs v-model:value="activeSubtab">
				<TabList>
					<Tab v-for="(tab, index) in tabs" :value="index" @click="changeTab(index)" :disabled="!tab.enabled">
						<FontAwesomeIcon :icon="tab.icon === 'lightbulb-on' ? ['fas', tab.icon] : ['far', tab.icon]" class="mr-2" v-if="tab.icon" />
						{{ tab.tabName }}
					</Tab>
				</TabList>
				<TabPanels>
					<TabPanel v-for="(tab, index) in tabs" :value="index">
						<component
							:is="tab.tabPage"
							:assessmentId="assessmentId"
							:tabName="tabName"
							:subTabName="tab.tabName"
							:tabId="tabId"
							:isCollapsed="isCollapsed"
							:steps="steps"
							:intermediates="intermediates"
							:ref="tab.tabName + '_' + assessmentId"
							:allTabsInformation="props.allTabsInformation"
							:highlightedStepId="highlightedStepId"
							:isTemplateCopyEnabled="isTemplateCopyEnabled"
							@updateAllTabsInformation="updateAllTabsInformation"
							@updateAllTabsInformationPerMainTab="updateAllTabsInformationPerMainTab"
							@materialAdded="materialAdded"
							@materialRemoved="materialRemoved"
							@rawMaterialUpdated="rawMaterialUpdated"
							@stepHovered="onStepHoveredFromList"
						></component> </TabPanel
				></TabPanels>
			</Tabs>
		</div>
		<!-- STEPS  -->
		<div
			class="list-view"
			:class="{ collapsed: isCollapsed, 'w-5': isCollapsed, 'w-[30%]': !isCollapsed }"
			v-if="
				generalInfomationId &&
				tabName !== tabsDefinitions.packaging.value &&
				tabName !== tabsDefinitions.distribution.value &&
				steps.length > 0 &&
				!shouldHideListView()
			"
		>
			<div class="flex items-center h-10">
				<Button id="btnAddStep" v-if="!isCollapsed" class="btn inverted" @click="addStep()" v-bind="lock.button.value" v-tooltip="lock.tooltip"
					><font-awesome-icon :icon="['far', 'circle-plus']" />Add Step</Button
				>
				<div v-else class="mr-3" :v-tooltip="{ content: 'Add new step', position: 'left' }">
					<Button
						id="btnAddStep"
						class="btn inverted"
						@click="addStep()"
						v-tooltip.top="'Add new step'"
						v-bind="lock.button.value"
						v-tooltip="lock.tooltip"
						><font-awesome-icon :icon="['far', 'circle-plus']"
					/></Button>
				</div>
				<div class="flex-grow" />
				<Button
					v-if="!isCollapsed"
					class="btn inverted mr-1"
					@click="isMagnifierActive = !isMagnifierActive"
					v-tooltip.top="isMagnifierActive ? 'Disable magnifier' : 'Enable magnifier'"
				>
					<font-awesome-icon :icon="['far', 'magnifying-glass-plus']" :class="{ 'text-blue-600': isMagnifierActive }" />
				</Button>
				<Button v-if="!isCollapsed" class="btn inverted mr-1" @click="fitMiniGraph" v-tooltip.top="'Fit graph to view'">
					<font-awesome-icon :icon="['far', 'compress']" />
				</Button>
				<Button
					v-if="!isCollapsed"
					class="btn inverted mr-1"
					@click="toggleCompactLayout"
					v-tooltip.top="isCompactLayout ? 'Wide layout' : 'Compact layout'"
				>
					<font-awesome-icon :icon="['far', 'arrows-to-line']" :class="{ 'text-blue-600': isCompactLayout }" />
				</Button>
				<Button v-if="!isCollapsed" class="btn inverted mr-1" @click="stepGraphExpandClick">
					<font-awesome-icon :icon="['far', 'arrows-maximize']" />
				</Button>
				<!-- <Button @click="test">aa</Button> -->
				<Button
					@click="toggleSteps"
					text
					class="btn inverted"
					style="color: var(--primary)"
					v-tooltip="isCollapsed ? 'Show all assessment\'s steps' : 'Hide all assessment\'s steps'"
				>
					<font-awesome-icon :icon="['far', isCollapsed ? 'stairs' : 'chevrons-right']" />
				</Button>
			</div>
			<div v-if="showPositionHint || hasUnsavedPositions" class="position-hint-banner">
				<span class="text-xs">Layout changed. Save with</span>
				<Button class="btn inverted position-save-btn" @click="persistGraphPositions" v-tooltip.top="'Save layout positions'">
					<font-awesome-icon :icon="['far', 'floppy-disk']" /> Save Layout
				</Button>
				<span class="text-xs">or via General Information Save.</span>
			</div>
			<div ref="graphContainerRef" class="h-[calc(100vh-120px)] min-h-[800px]" @mouseover="onGraphNodeMouseOver" @mouseleave="onGraphNodeMouseLeave">
				<VueFlowGraph
					ref="miniGraphRef"
					:key="miniGraphKey"
					id="mini-step-graph"
					:nodes="miniGraph.nodes"
					:edges="miniGraph.edges"
					:disabled="isStepLoading"
					:fit-view-on-init="!savedViewportState"
					:default-viewport="savedViewportState ?? undefined"
					@node-click="stepGraphNodeClick"
					@node-drag-start="handleNodeDragStart"
					@node-drag-stop="handleNodeDragStop"
					@viewport-change="handleViewportChange"
					@pane-ready="onMiniGraphPaneReady"
				/>
				<VueFlowMagnifier :active="isMagnifierActive" :graphRef="miniGraphRef" targetSelector="#mini-step-graph-container" />
			</div>
			<Dialog v-model:visible="showExpandedStepsFlow" :pt="{ transition: { onAfterEnter: graphModalAfterEnter } }" :closable="false" modal>
				<div class="w-[50vw] h-[70vh]">
					<VueFlowGraph
						:key="expandedGraphKey"
						id="expanded-step-graph"
						:nodes="expandedGraph.nodes"
						:edges="expandedGraph.edges"
						interactive
						@node-drag-start="handleNodeDragStart"
						@node-drag-stop="handleNodeDragStop"
					/>
				</div>
				<template #header>
					<div class="flex justify-between w-full">
						<PageLabel>
							<h1>Steps Flow</h1>
						</PageLabel>
						<div
							class="rounded-full border border-solid border-blue-500 w-[30px] h-[30px] flex items-center justify-center cursor-pointer hover:bg-blue-100"
							@click="() => (showExpandedStepsFlow = false)"
						>
							<FontAwesomeIcon class="text-blue-500" :icon="['far', 'xmark']" />
						</div>
					</div>
				</template>
			</Dialog>
		</div>
		<!-- COPY GENERAL INFORMATION DIALOG -->
		<Dialog v-model:visible="showCopyDialog" modal header="Copy Option" :style="{ width: '400px' }">
			<div class="flex flex-col gap-4">
				<div class="flex flex-col gap-2">
					<label for="copyName" class="font-medium">New Option Name</label>
					<InputText
						id="copyName"
						v-model="copyNewName"
						placeholder="Enter name for the copy"
						class="w-full"
						:class="{ 'p-invalid': copyNameAlreadyExists }"
					/>
					<small v-if="copyNameAlreadyExists" class="text-red-500">An option with this name already exists.</small>
				</div>
			</div>
			<template #footer>
				<div class="flex justify-end gap-2">
					<Button label="Cancel" class="btn inverted" @click="closeCopyDialog" />
					<Button label="Copy" class="btn" @click="executeCopy" :disabled="!copyNewName || isCopying || copyNameAlreadyExists" :loading="isCopying" />
				</div>
			</template>
		</Dialog>
		<!-- GENERAL INFORMATION OPTIONS - Only for Packaging and Distribution tabs when multi-option feature is enabled -->
		<div
			class="list-view px-2"
			:class="{
				collapsed: isGeneralInformationOptionsCollapsed,
				'w-5': isGeneralInformationOptionsCollapsed,
				'w-20': !isGeneralInformationOptionsCollapsed,
			}"
			v-if="
				isMultiOptionFeatureEnabled &&
				(props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value) &&
				!isResultSubtab &&
				!isCompareSubtab
			"
		>
			<div class="flex items-center">
				<b v-if="!isGeneralInformationOptionsCollapsed" class="text-[var(--primary)]">
					{{ props.tabName === tabsDefinitions.packaging.value ? "Packaging Options" : "Distribution Options" }}
				</b>
				<div class="flex-grow" />
				<Button
					@click="isGeneralInformationOptionsCollapsed = !isGeneralInformationOptionsCollapsed"
					text
					class="btn inverted"
					style="color: var(--primary)"
					v-tooltip="
						isGeneralInformationOptionsCollapsed
							? props.tabName === tabsDefinitions.packaging.value
								? 'Show packaging options'
								: 'Show distribution options'
							: props.tabName === tabsDefinitions.packaging.value
								? 'Hide packaging options'
								: 'Hide distribution options'
					"
				>
					<font-awesome-icon :icon="['far', isGeneralInformationOptionsCollapsed ? 'box-open' : 'chevrons-right']" />
				</Button>
			</div>
			<div class="flex flex-col gap-2" v-if="!isGeneralInformationOptionsCollapsed">
				<div class="flex gap-2">
					<Button class="btn inverted" @click="addGeneralInformationOption" v-bind="lock.button.value" v-tooltip="lock.tooltip">
						<font-awesome-icon :icon="['far', 'circle-plus']" class="" />Add
					</Button>
					<Button class="btn inverted" @click="copyGeneralInformationOption" v-bind="lock.button.value" v-tooltip="lock.tooltip">
						<font-awesome-icon :icon="['far', 'copy']" class="" />Copy
					</Button>
				</div>
				<div class="general-info-listbox-container" :class="{ 'is-loading': isSelectingOption }">
					<ul class="general-info-list" role="listbox">
						<li
							v-for="(option, index) in generalInformationOptions"
							:key="option.id"
							class="general-info-item"
							:class="{
								selected: selectedGeneralInformationOption === option.id,
								disabled: isSelectingOption && selectedGeneralInformationOption !== option.id,
							}"
							:aria-selected="selectedGeneralInformationOption === option.id"
							:aria-disabled="isSelectingOption"
							role="option"
							@click.stop.prevent="selectGeneralInformationOption(option)"
						>
							<div class="item-content">
								<span class="item-icon">
									<font-awesome-icon
										v-if="!(isSelectingOption && selectedGeneralInformationOption === option.id)"
										:icon="['far', selectedGeneralInformationOption === option.id ? 'circle-check' : 'circle']"
										class="icon"
									/>
									<font-awesome-icon v-else :icon="['far', 'spinner']" class="icon spinning" />
								</span>
								<div class="item-text-container">
									<span class="item-text">{{ option.generalInformationName || option.versionName || "Option " + (index + 1) }}</span>
									<I2TimestampDisplay class="item-timestamp" :date="option.createdTimeStamp" :user="option.createdByPerson" :singleLine="true" />
								</div>
							</div>
							<span v-if="selectedGeneralInformationOption === option.id && !isSelectingOption" class="selected-indicator">
								<font-awesome-icon :icon="['fas', 'caret-right']" />
							</span>
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick, provide } from "vue"
import { useEventTracking } from "@/composables/eventTracking"
import Tabs from "primevue/tabs"
import TabList from "primevue/tablist"
import Tab from "primevue/tab"
import TabPanels from "primevue/tabpanels"
import TabPanel from "primevue/tabpanel"
import Listbox from "primevue/listbox"
import {
	ApiClient,
	GeneralInformation,
	GeneralInformationClient,
	CopyGeneralInformationRequest,
	IntermediateStep,
	RawMaterial,
	RawMaterialTableRow,
	Step,
	FeatureFlagClient,
} from "../../entities/NSwagGenerated"
import { EventBus } from "../eventBus"
import Button from "primevue/button"
import assessmentTabResult from "./assessmentTabResult.vue"
import { library } from "@fortawesome/fontawesome-svg-core"
import VueFlowGraph from "./VueFlowGraph.vue"
import VueFlowMagnifier from "./VueFlowMagnifier.vue"
import Dialog from "primevue/dialog"
import InputText from "primevue/inputtext"
import I2TimestampDisplay from "./icComponents/I2TimestampDisplay.vue"
import {
	faInfoCircle,
	faTrash,
	faPen,
	faChartLine,
	faFileLines,
	faFlask,
	faLightbulbOn,
	faSackXmark,
	faSquarePollVertical,
	faStairs,
	faArrowsMaximize,
	faBoxOpen,
	faCopy,
	faCircle,
	faCircleCheck,
	faSpinner,
	faCirclePlus,
	faArrowsToLine,
	faFloppyDisk,
	faCompress,
	faMagnifyingGlassPlus,
} from "@fortawesome/pro-regular-svg-icons"
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons"
library.add(
	faTrash,
	faPen,
	faInfoCircle,
	faChartLine,
	faFileLines,
	faFlask,
	faLightbulbOn,
	faSackXmark,
	faSquarePollVertical,
	faStairs,
	faArrowsMaximize,
	faBoxOpen,
	faCircle,
	faCircleCheck,
	faSpinner,
	faCaretRight,
	faCopy,
	faCirclePlus,
	faArrowsToLine,
	faFloppyDisk,
	faCompress,
	faMagnifyingGlassPlus
)

import { useTabsDefinitions } from "../composables/tabsDefinitions"
import { layout, type LayoutGroup } from "../composables/elkLayout"
import { buildTemplateGroupColorMap } from "../composables/stepStyles"
import { Edge, Node } from "@vue-flow/core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { useToast } from "primevue/usetoast"
import { useAssessmentStore } from "@/store/assessmentStore"
//import { Nullable } from "primevue/ts-helpers"
import { useAssessmentLock } from "@/composables/assessmentLock"

const assessmentStore = useAssessmentStore()
const lock = useAssessmentLock(assessmentStore)
const toast = useToast()
const tabsDefinitions = useTabsDefinitions()

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const generalInformationClient = new GeneralInformationClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())

// Feature flag for multi-option on Packaging and Distribution
const isMultiOptionFeatureEnabled = ref(false)

// Feature flag for template copy in Synthetic API
const isTemplateCopyEnabled = ref(false)

// Function to load the multi-option feature flag
async function loadMultiOptionFeatureFlag() {
	if (props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value) {
		try {
			const featureFlag = await featureFlagClient.getByName("Allow Multiple Packaging Options per Assessment")
			isMultiOptionFeatureEnabled.value = featureFlag.isEnabled || false
			// Emit event to notify assessmentTabGeneralInformationv1 about the feature flag status
			EventBus.emit("multiOptionFeatureFlagLoaded_" + props.tabId, isMultiOptionFeatureEnabled.value)
		} catch (error) {
			// If the feature flag doesn't exist or there's an error, default to false
			isMultiOptionFeatureEnabled.value = false
			EventBus.emit("multiOptionFeatureFlagLoaded_" + props.tabId, false)
			console.warn("Feature flag 'Allow Multiple Packaging Options per Assessment' not found or error occurred:", error)
		}
	}
}

// Function to load the template copy feature flag
async function loadTemplateCopyFeatureFlag() {
	if (props.tabName === tabsDefinitions.syntheticAPI.value) {
		try {
			const featureFlag = await featureFlagClient.getByName("Use Template in Step Copy in Synthetic API")
			isTemplateCopyEnabled.value = featureFlag.isEnabled || false
		} catch (error) {
			isTemplateCopyEnabled.value = false
			console.warn("Feature flag 'Use Template in Step Copy in Synthetic API' not found or error occurred:", error)
		}
	}
}

//STEPS

const selectedStep = ref()
selectedStep.value = ""

const stepClicked = ref(false)

// Flag to prevent rapid step clicks while loading
const isStepLoading = ref(false)
// Timestamp of last step click to implement debounce
let lastStepClickTime = 0
const STEP_CLICK_DEBOUNCE_MS = 1000 // Increased to give child components time to load

const activeSubtab = ref(0)

// Project information for utilities check
let projectInfo = ref<any>(null)

let steps = ref<Step[]>([])
let intermediates = ref<IntermediateStep[]>([])
let materialEntryCount = ref<{ [key: string]: number }>({})

let showExpandedStepsFlow = ref(false)
let expandedGraphKey = ref(0)
let miniGraphKey = ref(0)

// General Information Options (for Packaging and Distribution tabs)
const isGeneralInformationOptionsCollapsed = ref(false)
const generalInformationOptions = ref<GeneralInformation[]>([])
const selectedGeneralInformationOption = ref<string | null>(null)
provide("packagingOptions", generalInformationOptions)
provide("selectedPackagingOption", selectedGeneralInformationOption)
const isSelectingOption = ref(false)

// Check if current subtab is "Result", "Results", or "Report"
const isResultSubtab = computed(() => {
	const currentSubtabIndex = activeSubtab.value
	const currentSubtabName = tabs.value && tabs.value[currentSubtabIndex] ? tabs.value[currentSubtabIndex].tabName : ""
	return currentSubtabName === "Result" || currentSubtabName === "Results" || currentSubtabName === "Report"
})

// Check if current subtab is "Compare"
const isCompareSubtab = computed(() => {
	const currentSubtabIndex = activeSubtab.value
	const currentSubtabName = tabs.value && tabs.value[currentSubtabIndex] ? tabs.value[currentSubtabIndex].tabName : ""
	return currentSubtabName === "Compare"
})

// Computed class for tab-view width
const tabViewWidthClass = computed(() => {
	const isPackagingOrDistribution = props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value
	const isLifecycleOrEol = props.tabName === tabsDefinitions.lifecycleSummaryResult.value || props.tabName === tabsDefinitions.consumerEndOfLife.value

	// For Packaging compare tab
	if (isPackagingOrDistribution && isCompareSubtab.value) {
		return "w-100"
	}

	// For Packaging and Distribution tabs with multi-option feature enabled
	if (isPackagingOrDistribution && isMultiOptionFeatureEnabled.value && !isResultSubtab.value) {
		return isGeneralInformationOptionsCollapsed.value ? "w-95" : "w-80"
	}

	// For Packaging and Distribution tabs with multi-option feature disabled - no list view, full width
	if (isPackagingOrDistribution && !isMultiOptionFeatureEnabled.value) {
		return "w-100"
	}

	// For Lifecycle Summary Result and Consumer End of Life tabs
	if (isLifecycleOrEol) {
		return "w-100"
	}

	// For other tabs with steps list view
	if (!shouldHideListView()) {
		return isCollapsed.value ? "w-95" : "w-70"
	}

	return "w-100"
})

// Copy dialog state and methods
const showCopyDialog = ref(false)
const copyNewName = ref("")
const isCopying = ref(false)
const copyNameAlreadyExists = computed(() => {
	if (!copyNewName.value) return false
	return generalInformationOptions.value.some((gi) => (gi.generalInformationName || gi.versionName) === copyNewName.value.trim())
})

const selectGeneralInformationOption = async (option: GeneralInformation) => {
	// Prevent multiple clicks while processing
	if (isSelectingOption.value) {
		return
	}

	// Don't do anything if the same option is already selected
	if (selectedGeneralInformationOption.value === option.id) {
		return
	}

	// Validate option
	if (!option || !option.id) {
		console.error("Invalid option selected")
		return
	}

	isSelectingOption.value = true

	try {
		// Update UI immediately for responsiveness
		selectedGeneralInformationOption.value = option.id

		// Update wasSelected in the database
		try {
			await apiClient.setGeneralInformationSelected(option.id)
		} catch (error) {
			console.error("Error updating wasSelected:", error)
		}

		// Reset steps and intermediates for the new selection
		steps.value = []
		intermediates.value = []

		// Force reset results since GeneralInformation changed
		EventBus.emit("forceResetResults_" + props.tabId)

		// Reset to General Information subtab
		activeSubtab.value = 0

		// Disable all subtabs except General Information and Report (will be re-enabled after load)
		if (props.fullTabs && props.fullTabs[props.selectedMainTabIndex] && props.fullTabs[props.selectedMainTabIndex].tabs) {
			props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
				if (index !== 0) {
					element.enabled = false
				}
			})
		}

		// Update the generalInformationId in allTabsStatus so validation uses the correct ID
		setTabInfoGlobally("generalInformationId", props.tabId, option.id)

		// Emit event to load the selected GeneralInformation in the form
		await nextTick()
		EventBus.emit("loadGeneralInformationById_" + props.tabId, option.id)
	} catch (error) {
		console.error("Error selecting GeneralInformation option:", error)
	} finally {
		// Small delay before allowing another selection to prevent rapid clicks
		setTimeout(() => {
			isSelectingOption.value = false
		}, 300)
	}
}

const onGeneralInformationOptionChange = (newValue: string | null) => {
	// Prevent deselection - only update if a value is selected
	if (newValue !== null) {
		selectedGeneralInformationOption.value = newValue
	}
}

const addGeneralInformationOption = () => {
	// Emit event to reset the general information form
	EventBus.emit("resetGeneralInformationForm_" + props.tabId)
	// Deselect current option
	selectedGeneralInformationOption.value = null
}

const copyGeneralInformationOption = () => {
	// Check if there's a selected option to copy
	if (!selectedGeneralInformationOption.value) {
		toast.add({ severity: "warn", summary: "No option selected", detail: "Please select an option to copy", life: 3000 })
		return
	}
	// Find the current option to suggest a name
	const currentOption = generalInformationOptions.value.find((gi) => gi.id === selectedGeneralInformationOption.value)
	const currentName = currentOption?.generalInformationName || currentOption?.versionName || "Option"
	const existingNames = new Set(generalInformationOptions.value.map((gi) => gi.generalInformationName || gi.versionName))
	let proposedName = `${currentName} - Copy`
	let suffix = 2
	while (existingNames.has(proposedName)) {
		proposedName = `${currentName} - Copy${suffix}`
		suffix++
	}
	copyNewName.value = proposedName
	showCopyDialog.value = true
}

const closeCopyDialog = () => {
	showCopyDialog.value = false
	copyNewName.value = ""
}

const executeCopy = async () => {
	if (!selectedGeneralInformationOption.value || !copyNewName.value) {
		return
	}

	isCopying.value = true
	try {
		const request = new CopyGeneralInformationRequest()
		request.sourceGeneralInformationId = selectedGeneralInformationOption.value
		request.newName = copyNewName.value

		const response = await generalInformationClient.copy(request)

		if (response.newGeneralInformationId) {
			const copySuccessMessage =
				props.tabName === tabsDefinitions.distribution.value ? "Distribution copied successfully." : "Device & Packaging copied successfully."
			toast.add({ severity: "success", summary: "Copy successful", detail: copySuccessMessage, life: 3000 })

			// Close the dialog
			closeCopyDialog()

			// Refresh the options list
			await loadGeneralInformationOptions()

			// Select the new option
			selectedGeneralInformationOption.value = response.newGeneralInformationId

			// Reset steps and intermediates for the new selection
			steps.value = []
			intermediates.value = []

			// Reset to General Information subtab
			activeSubtab.value = 0

			// Emit event to load the new GeneralInformation in the form
			await nextTick()
			EventBus.emit("loadGeneralInformationById_" + props.tabId, response.newGeneralInformationId)
		}
	} catch (error) {
		console.error("Error copying GeneralInformation:", error)
		toast.add({ severity: "error", summary: "Copy failed", detail: "An error occurred while copying the option", life: 5000 })
	} finally {
		isCopying.value = false
	}
}

const props = defineProps({
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	tabId: {
		type: String,
		required: true,
		default: "",
	},
	assessmentId: {
		type: String,
		required: true,
		default: "",
	},
	tabs: {
		type: Object,
		required: true,
		default: "",
	},
	fullTabs: {
		type: Object,
		required: true,
		default: "",
	},
	selectedMainTabIndex: {
		type: Number,
		required: true,
		default: 0,
	},
	activeTab: {
		type: Number,
		required: true,
		default: 0,
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: "",
	},
})

const loadGeneralInformationOptions = async (emitLoadEvent: boolean = false) => {
	if (props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value) {
		try {
			const result = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
			// Filter out consumer end of life entries (notes containing "consumerEndOfLife_" followed by a GUID)
			const consumerEndOfLifePattern = /^consumerEndOfLife_[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
			const filteredResult = result.filter((gi) => !gi.notes || !consumerEndOfLifePattern.test(gi.notes))
			generalInformationOptions.value = filteredResult
			// Select the option with wasSelected = true, or fall back to the first option
			// Also re-select if the currently selected option is no longer in the list (e.g., after deletion)
			const currentSelectionStillExists = filteredResult.some((gi) => gi.id === selectedGeneralInformationOption.value)
			if (filteredResult.length > 0 && (!selectedGeneralInformationOption.value || !currentSelectionStillExists)) {
				// Find the option that was previously selected (wasSelected = true)
				const selectedOption = filteredResult.find((gi) => gi.wasSelected === true) || filteredResult[0]
				selectedGeneralInformationOption.value = selectedOption.id ?? null
				// Emit load event if requested (e.g., after initial mount) or when selection changed due to deletion
				if ((emitLoadEvent || !currentSelectionStillExists) && selectedOption.id) {
					EventBus.emit("loadGeneralInformationById_" + props.tabId, selectedOption.id)
				}
			}
		} catch (error) {
			console.error("Error loading general information options:", error)
		}
	}
}

const tabName = ref()
const tabs = ref()
const tabId = ref()
const fullTabs = ref()
tabName.value = ""
const selectedMainTab = ref()

//steps
const isCollapsed = ref(false)
const isCompactLayout = ref(false)
const isMagnifierActive = ref(false)
const pendingLayoutCapture = ref(false)
function toggleCompactLayout() {
	isCompactLayout.value = !isCompactLayout.value
	// Clear saved positions so the new layout takes full effect
	savedNodePositions.value = {}
	// Flag to capture positions after the watcher recomputes layout
	pendingLayoutCapture.value = true
}

// Saved node positions and viewport from database
const savedNodePositions = ref<Record<string, { x: number; y: number }>>({})
const savedViewportState = ref<{ x: number; y: number; zoom: number } | null>(null)
const hasUnsavedPositions = ref(false)
const showPositionHint = ref(false)
let positionHintTimer: ReturnType<typeof setTimeout> | null = null
let ignoreViewportChanges = false

async function loadSavedPositions() {
	if (!generalInfomationIdReal.value) return
	try {
		const json = await generalInformationClient.graphPositionsGet(generalInfomationIdReal.value)
		if (json) {
			const parsed = JSON.parse(json)
			if (parsed.__viewport) {
				savedViewportState.value = parsed.__viewport
				delete parsed.__viewport
			} else {
				savedViewportState.value = null
			}
			if (parsed.__compact !== undefined) {
				isCompactLayout.value = parsed.__compact
				delete parsed.__compact
			}
			savedNodePositions.value = parsed
		} else {
			savedNodePositions.value = {}
			savedViewportState.value = null
		}
	} catch {
		savedNodePositions.value = {}
		savedViewportState.value = null
	}
	hasUnsavedPositions.value = false
}

function captureNodePositions(nodes: Node[]) {
	const positions: Record<string, { x: number; y: number }> = {}
	// Build a map for quick parent lookup
	const nodeMap = new Map<string, Node>()
	for (const node of nodes) nodeMap.set(node.id, node)
	for (const node of nodes) {
		if (node.type === "template-group") continue
		// Convert relative position back to absolute if node has a parent
		let x = node.position.x
		let y = node.position.y
		if (node.parentNode) {
			const parent = nodeMap.get(node.parentNode)
			if (parent) {
				x += parent.position.x
				y += parent.position.y
			}
		}
		positions[node.id] = { x, y }
	}
	savedNodePositions.value = positions
	hasUnsavedPositions.value = true
	// Show the hint message for 5 seconds
	showPositionHint.value = true
	if (positionHintTimer) clearTimeout(positionHintTimer)
	positionHintTimer = setTimeout(() => {
		showPositionHint.value = false
	}, 5000)
}

async function persistGraphPositions() {
	if (!generalInfomationIdReal.value || !hasUnsavedPositions.value) return
	try {
		// Capture current node positions from the graph if not already captured by drag
		if (Object.keys(savedNodePositions.value).length === 0 && miniGraphRef.value) {
			captureNodePositions(miniGraphRef.value.getNodes.value)
		}
		// Include viewport state and compact layout flag in the saved JSON
		const dataToSave = { ...savedNodePositions.value } as Record<string, any>
		if (miniGraphRef.value) {
			dataToSave.__viewport = miniGraphRef.value.getViewport()
		}
		dataToSave.__compact = isCompactLayout.value
		await generalInformationClient.graphPositionsPost(generalInfomationIdReal.value, JSON.stringify(dataToSave))
		// Update savedViewportState so subsequent re-renders (e.g. step selection) restore the correct viewport
		if (dataToSave.__viewport) {
			savedViewportState.value = dataToSave.__viewport
		}
		hasUnsavedPositions.value = false
		showPositionHint.value = false
		toast.add({ severity: "success", summary: "Layout saved!", life: 2000 })
	} catch (err) {
		console.error("Failed to save graph positions:", err)
		toast.add({ severity: "error", summary: "Failed to save layout", life: 3000 })
	}
}

function handleNodeDragStart(_event: { node: Node }) {
	// Reserved for future use
}

function compactNonGroupNodesAfterDrag(draggedGroupNode: Node, allNodes: Node[], edges: Edge[]) {
	const draggedGroupId = draggedGroupNode.id
	const nodeMap = new Map<string, Node>()
	for (const n of allNodes) nodeMap.set(n.id, n)

	// Collect top-level non-group nodes (not children of the dragged group, not template-group nodes)
	// For nodes inside OTHER template groups, we work with the group as a unit
	const freeNodes: Node[] = []
	const otherGroupIds = new Set<string>()
	for (const n of allNodes) {
		if (n.id === draggedGroupId) continue
		if (n.parentNode === draggedGroupId) continue
		if (n.type === "template-group") {
			otherGroupIds.add(n.id)
			continue
		}
		if (n.parentNode && otherGroupIds.has(n.parentNode)) continue
		if (n.parentNode) continue // skip children of other groups, handle via group
		freeNodes.push(n)
	}

	// Also collect other template groups as movable units
	const otherGroups = allNodes.filter((n) => n.type === "template-group" && n.id !== draggedGroupId)

	// Build "components" - each is a set of connected free nodes or a single template group
	// Use union-find on free nodes based on edges
	const freeNodeIds = freeNodes.map((n) => n.id)
	const idToIndex = new Map<string, number>()
	freeNodeIds.forEach((id, i) => idToIndex.set(id, i))

	const parent = freeNodeIds.map((_, i) => i)
	const find = (x: number): number => {
		while (parent[x] !== x) {
			parent[x] = parent[parent[x]]
			x = parent[x]
		}
		return x
	}
	const union = (a: number, b: number) => {
		const ra = find(a),
			rb = find(b)
		if (ra !== rb) parent[ra] = rb
	}

	for (const e of edges) {
		const si = idToIndex.get(e.source)
		const ti = idToIndex.get(e.target)
		if (si !== undefined && ti !== undefined) union(si, ti)
	}

	// Group free nodes into connected components
	const componentMap = new Map<number, Node[]>()
	for (let i = 0; i < freeNodes.length; i++) {
		const root = find(i)
		if (!componentMap.has(root)) componentMap.set(root, [])
		componentMap.get(root)!.push(freeNodes[i])
	}

	// Build a list of "blocks" (each block = connected component of free nodes OR an other-template-group)
	type Block = {
		nodes: Node[]
		minX: number
		minY: number
		maxX: number
		maxY: number
		width: number
		height: number
		isGroup: boolean
		groupNode?: Node
	}
	const blocks: Block[] = []

	for (const compNodes of componentMap.values()) {
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity
		for (const n of compNodes) {
			const x = n.position?.x ?? 0
			const y = n.position?.y ?? 0
			const w = (n.width as number) || 75
			const h = (n.height as number) || 45
			minX = Math.min(minX, x)
			minY = Math.min(minY, y)
			maxX = Math.max(maxX, x + w)
			maxY = Math.max(maxY, y + h)
		}
		blocks.push({ nodes: compNodes, minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY, isGroup: false })
	}

	for (const g of otherGroups) {
		const gx = g.position?.x ?? 0
		const gy = g.position?.y ?? 0
		const gw = (g.data?.width as number) || 150
		const gh = (g.data?.height as number) || 100
		const children = allNodes.filter((n) => n.parentNode === g.id)
		blocks.push({ nodes: children, minX: gx, minY: gy, maxX: gx + gw, maxY: gy + gh, width: gw, height: gh, isGroup: true, groupNode: g })
	}

	if (blocks.length === 0) return

	// Sort blocks by their current vertical midpoint to preserve relative ordering
	blocks.sort((a, b) => (a.minY + a.maxY) / 2 - (b.minY + b.maxY) / 2)

	// Also include the dragged group as a reference for vertical stacking
	const draggedGx = draggedGroupNode.position?.x ?? 0
	const draggedGy = draggedGroupNode.position?.y ?? 0
	const draggedGw = (draggedGroupNode.data?.width as number) || 150
	const draggedGh = (draggedGroupNode.data?.height as number) || 100
	const draggedBlock = {
		minX: draggedGx,
		minY: draggedGy,
		maxX: draggedGx + draggedGw,
		maxY: draggedGy + draggedGh,
		width: draggedGw,
		height: draggedGh,
	}

	// Merge dragged group into the sorted block list to determine correct stacking position
	type StackItem = { block?: Block; isDragged: boolean; minY: number; maxY: number; height: number; midY: number }
	const stackItems: StackItem[] = blocks.map((b) => ({
		block: b,
		isDragged: false,
		minY: b.minY,
		maxY: b.maxY,
		height: b.height,
		midY: (b.minY + b.maxY) / 2,
	}))
	stackItems.push({
		isDragged: true,
		minY: draggedBlock.minY,
		maxY: draggedBlock.maxY,
		height: draggedBlock.height,
		midY: (draggedBlock.minY + draggedBlock.maxY) / 2,
	})
	stackItems.sort((a, b) => a.midY - b.midY)

	// Stack all items vertically with a gap, keeping the dragged group at its user-chosen position
	// First pass: figure out where everything should go
	const gap = 50
	let currentY = stackItems[0].minY
	for (const item of stackItems) {
		if (item.isDragged) {
			// Keep dragged group where the user placed it, but update currentY
			currentY = item.maxY + gap
			continue
		}
		const block = item.block!
		const offsetY = currentY - block.minY
		const offsetX = 0 // preserve horizontal positions

		if (Math.abs(offsetY) > 2) {
			if (block.isGroup && block.groupNode) {
				// Move the group node, children stay relative
				block.groupNode.position = { x: block.groupNode.position.x + offsetX, y: block.groupNode.position.y + offsetY }
			} else {
				// Move all free nodes in this component
				for (const n of block.nodes) {
					n.position = { x: n.position.x + offsetX, y: n.position.y + offsetY }
				}
			}
		}
		currentY = currentY + block.height + gap
	}
}

function expandGroupToFitChildren(groupId: string, allNodes: Node[]) {
	const groupNode = allNodes.find((n) => n.id === groupId)
	if (!groupNode) return

	const children = allNodes.filter((n) => n.parentNode === groupId)
	if (children.length === 0) return

	const paddingX = 15
	const paddingTop = 30
	const paddingBottom = 15

	// Calculate bounding box from children's RELATIVE positions
	let minX = Infinity,
		minY = Infinity,
		maxX = -Infinity,
		maxY = -Infinity
	for (const child of children) {
		const x = child.position.x
		const y = child.position.y
		const w = child.dimensions?.width ?? 150
		const h = child.dimensions?.height ?? 40
		minX = Math.min(minX, x)
		minY = Math.min(minY, y)
		maxX = Math.max(maxX, x + w)
		maxY = Math.max(maxY, y + h)
	}

	// If children are dragged above/left of the padding zone, shift the group
	// position and adjust all children's relative positions to compensate
	const shiftX = minX < paddingX ? paddingX - minX : 0
	const shiftY = minY < paddingTop ? paddingTop - minY : 0

	if (shiftX > 0 || shiftY > 0) {
		groupNode.position.x -= shiftX
		groupNode.position.y -= shiftY
		for (const child of children) {
			child.position.x += shiftX
			child.position.y += shiftY
		}
		maxX += shiftX
		maxY += shiftY
	}

	// Update group dimensions to fit all children
	groupNode.data.width = maxX + paddingX
	groupNode.data.height = maxY + paddingBottom
}

function handleNodeDragStop(event: { node: Node; nodes: Node[] }) {
	if (event.node.type === "template-group") {
		compactNonGroupNodesAfterDrag(event.node, event.nodes, miniGraph.value.edges)
	}

	// Auto-expand parent group when a child node is dragged outside bounds
	if (event.node.parentNode) {
		expandGroupToFitChildren(event.node.parentNode, event.nodes)
	}

	captureNodePositions(event.nodes)
}

function handleViewportChange(_viewport: { x: number; y: number; zoom: number }) {
	// Ignore viewport changes triggered by magnifier or programmatic fitView
	if (isMagnifierActive.value || ignoreViewportChanges) return
	if (!hasUnsavedPositions.value) {
		hasUnsavedPositions.value = true
		showPositionHint.value = true
		if (positionHintTimer) clearTimeout(positionHintTimer)
		positionHintTimer = setTimeout(() => {
			showPositionHint.value = false
		}, 5000)
	}
}
const hideSecondTabMenu = ref()

const generalInfomationId = ref()
const generalInfomationIdReal = ref()
//const activeSubTab = ref([] as Object[])

const stepsMessage = ref("Loading...")

const actualStep = ref()

// Flag to control if Packaging Materials tab should always be enabled
const packagingMaterialsAlwaysEnabled = ref(false)

class tabActualStatus {
	actualMainTabId: string
	actualSubTabName: string
	selectedStepId: string
	generalInformationId: string
}

class allTabsActualStatus {
	public allTabsStatus: tabActualStatus[]
}
const emit = defineEmits(["updateAllTabsInformation", "updateAllTabsInformationPerMainTab"])
const materialAdded = (materialRow: RawMaterialTableRow) => {
	if (!materialRow.isNew) intermediates.value = intermediates.value.filter((i) => i.id !== materialRow.id)
	if (materialRow.isIntermediate)
		intermediates.value.push({
			id: materialRow.id,
			stepId: materialRow.stepId,
			intermediateStepId: materialRow.intermediateId,
			init: () => {},
			toJSON: () => {},
		})
	if (materialRow.isNew && materialEntryCount.value[materialRow.stepId ?? ""] != undefined) {
		const stepId = materialRow.stepId ?? ""
		const oldCount = materialEntryCount.value[stepId]
		materialEntryCount.value[stepId]++
		// Force reactivity by creating a new object
		materialEntryCount.value = { ...materialEntryCount.value }

		// If this was the first material added (0 -> 1), trigger graph regeneration
		if (oldCount === 0) {
			// Wait for Vue reactivity to complete, then regenerate the graph
			nextTick(async () => {
				miniGraph.value = await getGraphLayout(steps.value, selectedStep.value, intermediates.value, materialEntryCount.value, "DOWN", "mini")
				expandedGraph.value = await getGraphLayout(steps.value, selectedStep.value, intermediates.value, materialEntryCount.value, "DOWN", "expanded")
				miniGraphKey.value++
			})
		}
	}
}
const materialRemoved = (id: string, stepId: string) => {
	intermediates.value = intermediates.value.filter((i) => i.id !== id)
	if (materialEntryCount.value[stepId]) {
		const oldCount = materialEntryCount.value[stepId]
		materialEntryCount.value[stepId]--
		// Force reactivity by creating a new object
		materialEntryCount.value = { ...materialEntryCount.value }

		// If this was the last material removed (1 -> 0), trigger graph regeneration
		if (oldCount === 1) {
			// Wait for Vue reactivity to complete, then regenerate the graph
			nextTick(async () => {
				miniGraph.value = await getGraphLayout(steps.value, selectedStep.value, intermediates.value, materialEntryCount.value, "DOWN", "mini")
				expandedGraph.value = await getGraphLayout(steps.value, selectedStep.value, intermediates.value, materialEntryCount.value, "DOWN", "expanded")
				miniGraphKey.value++
			})
		}
	}
}
const rawMaterialUpdated = (rawMaterial: RawMaterial | undefined) => {
	if (!rawMaterial?.stepId) return

	// Find the step and update its rawMaterial to reflect the new state
	const step = steps.value.find((s) => s.id === rawMaterial.stepId)
	if (step) {
		const hadRawMaterial = step.rawMaterial && step.rawMaterial.length > 0
		const hasRawMaterial = rawMaterial.id !== undefined

		// Only update if the "has output" state changed to avoid unnecessary graph regeneration
		if (hadRawMaterial !== hasRawMaterial) {
			if (hasRawMaterial) {
				step.rawMaterial = [rawMaterial]
			} else {
				step.rawMaterial = []
			}
			// Force reactivity
			steps.value = [...steps.value]
		}
	}
}
const updateAllTabsInformation = (newTabsContents: any) => {
	emit("updateAllTabsInformation", newTabsContents)
}
const updateAllTabsInformationPerMainTab = (newTabsContents: any) => {
	emit("updateAllTabsInformationPerMainTab", newTabsContents)
}
const finalStep = computed(() =>
	steps?.value?.reduce((max: Step, step) => {
		return (step.stepNumber ?? 0 > (max.stepNumber ?? 0)) ? step : max
	})
)
let miniGraph = ref<{ nodes: Node[]; edges: Edge[] }>({ nodes: [], edges: [] })
let expandedGraph = ref<{ nodes: Node[]; edges: Edge[] }>({ nodes: [], edges: [] })
const miniGraphRef = ref<InstanceType<typeof VueFlowGraph> | null>(null)
const graphContainerRef = ref<HTMLElement | null>(null)

async function fitMiniGraph() {
	ignoreViewportChanges = true
	miniGraphRef.value?.fitView({ padding: 0.2 })
	await nextTick()
	ignoreViewportChanges = false
}

async function onMiniGraphPaneReady() {
	ignoreViewportChanges = true
	if (savedViewportState.value && miniGraphRef.value) {
		// Wait for VueFlow to finish its internal rendering before restoring viewport
		await nextTick()
		await nextTick()
		miniGraphRef.value?.setViewport(savedViewportState.value)
	}
	// Suppress viewport change events from both saved viewport restore and fit-view-on-init
	await nextTick()
	await nextTick()
	ignoreViewportChanges = false
}

// Step hover highlighting: list ↔ graph
const highlightedStepId = ref<string | null>(null)

watch(highlightedStepId, (newId, oldId) => {
	const container = graphContainerRef.value
	if (!container) return
	if (oldId) {
		const oldEl = container.querySelector(`.vue-flow__node[data-id="${oldId}"]`)
		oldEl?.classList.remove("step-node-highlighted")
	}
	if (newId) {
		const newEl = container.querySelector(`.vue-flow__node[data-id="${newId}"]`)
		newEl?.classList.add("step-node-highlighted")
	}
})

function onStepHoveredFromList(stepId: string | null) {
	highlightedStepId.value = stepId
}

function onGraphNodeMouseOver(event: MouseEvent) {
	const nodeEl = (event.target as HTMLElement).closest(".vue-flow__node")
	if (!nodeEl) {
		if (highlightedStepId.value) highlightedStepId.value = null
		return
	}
	const nodeId = (nodeEl as HTMLElement).dataset.id
	if (!nodeId || nodeId.startsWith("template-group-")) {
		if (highlightedStepId.value) highlightedStepId.value = null
		return
	}
	if (nodeId !== highlightedStepId.value) {
		highlightedStepId.value = nodeId
	}
}

function onGraphNodeMouseLeave() {
	if (highlightedStepId.value) {
		highlightedStepId.value = null
	}
}

// Counter to track graph layout updates and prevent race conditions
let graphLayoutVersion = 0

watch(
	[steps, selectedStep, intermediates, materialEntryCount, isCompactLayout],
	async ([newSteps, newSelectedStep, newIntermediates, newMaterialEntryCount, newCompact]) => {
		// Increment version for this update
		const currentVersion = ++graphLayoutVersion

		const [newMiniGraph, newExpandedGraph] = await Promise.all([
			getGraphLayout(newSteps, newSelectedStep, newIntermediates, newMaterialEntryCount, "DOWN", "mini", newCompact),
			getGraphLayout(newSteps, newSelectedStep, newIntermediates, newMaterialEntryCount, "DOWN", "expanded", newCompact),
		])

		// Only apply if this is still the latest update (no newer updates started)
		if (currentVersion === graphLayoutVersion) {
			miniGraph.value = newMiniGraph
			expandedGraph.value = newExpandedGraph
			miniGraphKey.value++

			// Viewport is handled by VueFlow: fit-view-on-init when no saved state,
			// or onMiniGraphPaneReady when saved viewport exists

			// After layout recomputation from compact toggle, capture the new positions
			if (pendingLayoutCapture.value) {
				pendingLayoutCapture.value = false
				captureNodePositions(newMiniGraph.nodes)
			}
		}
	},
	{ deep: true }
)
async function getGraphLayout(
	stepList: Step[],
	currentStep: Step,
	intermediateList: IntermediateStep[],
	materialEntryCountByStep: { [key: string]: number },
	direction: string,
	view: "mini" | "expanded",
	compact = false
) {
	if (stepList.length === 0) return { nodes: [], edges: [] }
	try {
		const edges = intermediateList.map(
			(i) =>
				({
					id: i.id,
					source: i.intermediateStepId,
					target: i.stepId,
					markerEnd: "arrowclosed",
				}) as Edge
		)
		const connectedNodes = connectedNodeFinder(finalStep.value?.id ?? "", edges, [])
		const canvas = document.createElement("canvas")
		const canvasContext = canvas.getContext("2d")
		if (canvasContext)
			canvasContext.font =
				'16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol'
		const nodes = stepList.map((s) => {
			let isFinalStep = s.id === finalStep.value?.id
			let isTarget = edges.some((e) => e.target === s.id)
			let isSource = edges.some((e) => e.source === s.id)

			let status = "healthy"
			let errors = [] as string[]
			if (s.screenId?.toLowerCase() === "533806da-4d58-465e-975a-006fb603b803") {
			} // Do nothing status is already healthy
			else if (isFinalStep) {
				status = "final"
			} else if (!isSource) {
				status = "error"
				errors.push("This step is not used in any step.")
			} else if (!connectedNodes.includes(s.id ?? "")) {
				status = "warning"
				errors.push("No path to the final step.")
			}
			if (!s.rawMaterial?.length && !props.tabName.includes("Biologics")) {
				status = "error"
				errors.push("The step output has not been configured yet.")
			}
			if (materialEntryCountByStep[s.id ?? ""] === undefined || materialEntryCountByStep[s.id ?? ""] === 0) {
				status = "error"
				errors.push("The step does not have any materials" + (props.tabName.includes("Biologics") ? "" : " or intermediates") + " added yet.")
			}

			let type = "step"
			if (s.screenId?.toLowerCase() === "533806da-4d58-465e-975a-006fb603b803") {
			} // Do nothing type is already step
			if (isFinalStep) type = "final"
			else if (isTarget) type = "has-intermediate"

			const result = {
				id: s.id,
				type: type,
				data: {
					label: view === "mini" ? s.stepNumber : `${s.stepNumber} - ${s.stepName}`,
					isSelected: s.id == currentStep?.id,
					isTarget,
					isSource,
					isFinalStep,
					status,
					errors,
					tooltip: `${s.stepNumber}-${s.stepName}`,
					isFromTemplate: isTemplateCopyEnabled.value && !!s.templateSourceStepId,
					templateGroupId: s.templateGroupId ?? null,
					templateSourceStepName: s.templateSourceStepName ?? null,
					templateSourceProjectName: s.templateSourceProjectName ?? null,
					templateSourceSubProjectName: s.templateSourceSubProjectName ?? null,
					templateSourceAssessmentName: s.templateSourceAssessmentName ?? null,
					templateSourceGraphJson: s.templateSourceGraphJson ?? null,
				},
			} as Node

			if (view === "expanded" && canvasContext) {
				const textMetrics = canvasContext.measureText(result.data.label)
				result.width = textMetrics.width * 1.2 + 20 // Add padding
			}

			return result
		})
		// Build template group info before layout so ELK can separate them
		const preGroupMap = new Map<string, Node[]>()
		const layoutGroups: LayoutGroup[] = []
		let groupColorMap = new Map<string, string>()
		if (isTemplateCopyEnabled.value) {
			for (const node of nodes) {
				const groupId = node.data?.templateGroupId
				if (groupId) {
					if (!preGroupMap.has(groupId)) preGroupMap.set(groupId, [])
					preGroupMap.get(groupId)!.push(node)
				}
			}
			// Compute group colors matching the step list row backgrounds
			groupColorMap = buildTemplateGroupColorMap(stepList)
			// Create layout groups for ELK compound nodes (only groups with 2+ nodes)
			for (const [groupId, groupNodes] of preGroupMap) {
				if (groupNodes.length < 2) continue
				layoutGroups.push({
					id: `template-group-${groupId}`,
					nodeIds: new Set(groupNodes.map((n) => n.id)),
				})
			}
		}

		var result = await layout(nodes, edges, direction, layoutGroups, compact)

		// Apply saved positions (override ELK-computed positions with user-adjusted ones)
		if (Object.keys(savedNodePositions.value).length > 0) {
			// Check if all current nodes have saved positions — if some are missing
			// (e.g. new steps were added from a template), the saved layout is stale
			const allNodesHaveSavedPositions = result.nodes.every((n) => savedNodePositions.value[n.id])
			if (allNodesHaveSavedPositions) {
				for (const node of result.nodes) {
					const saved = savedNodePositions.value[node.id]
					if (saved) {
						node.position = { x: saved.x, y: saved.y }
					}
				}
			} else {
				// Stale saved positions: discard them so the fresh ELK layout is used
				savedNodePositions.value = {}
				savedViewportState.value = null
			}
		}

		// Add template group background nodes and set up parent-child relationships
		if (isTemplateCopyEnabled.value)
			for (const [groupId, groupNodes] of preGroupMap) {
				if (groupNodes.length < 2) continue
				const templateName = groupNodes[0].data?.templateSourceStepName ?? "Template"

				// Find the laid-out positions of group member nodes
				const laidOutGroupNodes = result.nodes.filter((n) => groupNodes.some((gn) => gn.id === n.id))
				let minX = Infinity,
					minY = Infinity,
					maxX = -Infinity,
					maxY = -Infinity
				for (const n of laidOutGroupNodes) {
					const x = n.position?.x ?? 0
					const y = n.position?.y ?? 0
					const w = n.width ?? 150
					const h = n.height ?? 40
					if (x < minX) minX = x
					if (y < minY) minY = y
					if (x + w > maxX) maxX = x + w
					if (y + h > maxY) maxY = y + h
				}

				const paddingX = 15
				const paddingTop = 30
				const paddingBottom = 15

				const groupNodeId = `template-group-${groupId}`
				const groupX = minX - paddingX
				const groupY = minY - paddingTop

				// Parse the stored graph JSON snapshot for the mini-preview
				const graphJsonStr = laidOutGroupNodes.find((n) => n.data?.templateSourceGraphJson)?.data?.templateSourceGraphJson
				let originalGraph: { steps: { label: string }[]; connections: { from: number; to: number }[] } = { steps: [], connections: [] }
				let templateScreenName = ""
				if (graphJsonStr) {
					try {
						const graphData = JSON.parse(graphJsonStr)
						templateScreenName = graphData.steps?.[0]?.screenName ?? ""
						originalGraph = {
							steps: graphData.steps.map((s: any) => ({
								label: s.originalStepName ? `${s.originalStepName} (${s.label})` : s.label,
							})),
							connections: graphData.connections ?? [],
						}
					} catch {
						originalGraph = { steps: laidOutGroupNodes.map((n) => ({ label: n.data.label })), connections: [] }
					}
				} else {
					// Legacy fallback for steps copied before this feature
					originalGraph = { steps: laidOutGroupNodes.map((n) => ({ label: n.data.label })), connections: [] }
				}

				// Add group node first (parent must exist before children reference it)
				result.nodes.unshift({
					id: groupNodeId,
					type: "template-group",
					position: { x: groupX, y: groupY },
					data: {
						templateName,
						width: maxX - minX + paddingX * 2,
						height: maxY - minY + paddingTop + paddingBottom,
						groupColor: groupColorMap.get(groupId) ?? null,
						templateSourceProjectName: laidOutGroupNodes[0].data?.templateSourceProjectName ?? "",
						templateSourceSubProjectName: laidOutGroupNodes[0].data?.templateSourceSubProjectName ?? "",
						templateSourceAssessmentName: laidOutGroupNodes[0].data?.templateSourceAssessmentName ?? "",
						templateScreenName,
						originalGraph,
					},
					zIndex: -1,
					selectable: false,
					draggable: true,
				} as Node)

				// Convert child node positions to be relative to the group and set parentNode
				for (const n of laidOutGroupNodes) {
					n.parentNode = groupNodeId
					n.position = {
						x: (n.position?.x ?? 0) - groupX,
						y: (n.position?.y ?? 0) - groupY,
					}
				}
			}

		return result
	} catch (e) {
		toast.add({ severity: "error", summary: "Could not compute the step graph", life: 2000 })
		return { nodes: [], edges: [] }
	}
}
function connectedNodeFinder(nodeId: string, edges: Edge[], path: string[]): string[] {
	if (path.includes(nodeId)) return [] // Prevent cycle as node is already covered
	const newPath = [...path, nodeId]
	const directNodes = edges.filter((edge) => edge.target === nodeId).map((edge) => edge.source)
	const indirectNodes = directNodes.flatMap((n) => connectedNodeFinder(n, edges, newPath))
	const connectedNodes: string[] = [...new Set([...directNodes, ...indirectNodes])]
	return connectedNodes
}
onMounted(async () => {
	tabName.value = props.tabName
	tabs.value = props.tabs
	tabId.value = props.tabId
	fullTabs.value = props.fullTabs

	// Load feature flags
	await loadMultiOptionFeatureFlag()
	await loadTemplateCopyFeatureFlag()

	// Load general information options for Packaging and Distribution tabs
	loadGeneralInformationOptions()

	EventBus.on("restSelectedSubTab", () => {
		activeSubtab.value = 0
	})
	//load steps
	EventBus.on("loadStepListBox_" + props.tabId, async (data: any) => {
		generalInfomationId.value = data.generalInformationId !== "00000000-0000-0000-0000-000000000000"
		generalInfomationIdReal.value = data.generalInformationId
		await loadSavedPositions()
		getSteps(data.screenId, data.generalInformationId)
	})
	EventBus.on("enableOrDisableTabsAsRawMaterialContent_" + props.tabId, (status: any) => {
		var indexToFollow = tabsDefinitions.packaging.value === props.tabName ? 2 : 1
		props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
			if (index > indexToFollow) {
				element.enabled = status
			}
		})
	})

	EventBus.on("enableTabsWhenRawMaterialIsNotEmpty_" + props.tabId, () => {
		//var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus

		//selectedStepName.value = toGetStatus.actualSubTabName

		var actualStepId = props.allTabsInformation.allTabsStatus[props.selectedMainTabIndex].selectedStepId
		var actualGeneralInformation = props.allTabsInformation.allTabsStatus[props.selectedMainTabIndex].generalInformationId
		if (
			actualGeneralInformation === undefined ||
			actualStepId === undefined ||
			props.allTabsInformation.allTabsStatus[props.selectedMainTabIndex].tabId === undefined
		)
			return
		apiClient.getRawMaterialsByGeneraInformationIdScreenIdStepId(actualGeneralInformation.value, props.tabId, actualStepId, "").then((res) => {
			if (res !== null) {
				props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
					if (index > 1) {
						element.enabled = true
					}
				})
			} else {
				props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
					if (index > 1) {
						element.enabled = false
					}
				})
			}
		})
	})
	// Tab-specific event for resetting when Add is clicked
	EventBus.on("newGeneralInformation_" + props.tabId, () => {
		// Reset steps
		steps.value = []
		intermediates.value = []

		// Reset to General Information subtab
		activeSubtab.value = 0

		// Disable all subtabs except General Information (index 0)
		if (props.fullTabs && props.fullTabs[props.selectedMainTabIndex] && props.fullTabs[props.selectedMainTabIndex].tabs) {
			props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
				if (index !== 0) {
					element.enabled = false
				}
			})
		}

		// Reset the generalInformationId
		generalInfomationId.value = false
		generalInfomationIdReal.value = ""
	})

	// Global event for backwards compatibility (e.g., commonStep.vue still uses this)
	EventBus.on("newGeneralInformation", () => {
		steps.value = []
	})

	// Listen for refresh event to update the general information options list after save
	EventBus.on("refreshGeneralInformationOptions_" + props.tabId, async (savedId: unknown) => {
		await loadGeneralInformationOptions()
		// Select the newly saved item if ID is provided
		if (savedId && typeof savedId === "string") {
			selectedGeneralInformationOption.value = savedId
		}
	})

	// Save graph positions when General Information is saved
	EventBus.on("saveGraphPositions_" + props.tabId, async () => {
		if (hasUnsavedPositions.value) {
			await persistGraphPositions()
		}
	})

	EventBus.on("enableTabsAfterRawMaterial" + props.tabId, (stepId: any) => {
		apiClient.rawMaterialExist(stepId).then((rawMatExist) => {
			props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
				if (index !== 0) element.enabled = false
				if (rawMatExist) {
					if (index > 0) {
						if (props.tabName === tabsDefinitions.packaging.value) {
							apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId).then((res) => {
								if (index === 1) element.enabled = res[0].packDeviceUsed
								else element.enabled = true
							})
						} else {
							element.enabled = true
						}
					}
				} else {
					if (index === 1) {
						element.enabled = true
					}
				}
			})
		})
	})
	EventBus.on("nextPreviousTab_" + props.tabId, (data: any) => {
		if (data.action === "next") {
			if (activeSubtab.value === tabs.value.length - 1) return
			// return
			//verify if next tab is enabled
			activeSubtab.value = activeSubtab.value === undefined ? 0 : activeSubtab.value
			//if enabled go to the next tab
			if (props.fullTabs[props.selectedMainTabIndex].tabs[activeSubtab.value + 1].enabled) {
				activeSubtab.value = activeSubtab.value + 1
				changeTabByIndex(activeSubtab.value)
			} else {
				props.fullTabs[props.selectedMainTabIndex].tabs.length < activeSubtab.value + 1
					? (activeSubtab.value = activeSubtab.value)
					: (activeSubtab.value = activeSubtab.value + 2)
				changeTabByIndex(activeSubtab.value)
			}
		} else {
			//verify if prev tab is enabled
			if (activeSubtab.value === 0) return
			if (props.fullTabs[props.selectedMainTabIndex].tabs[activeSubtab.value - 1].enabled) {
				activeSubtab.value = activeSubtab.value !== undefined && activeSubtab.value > 0 ? activeSubtab.value - 1 : 0
			} else {
				if (activeSubtab.value > 1) activeSubtab.value = activeSubtab.value !== undefined && activeSubtab.value > 0 ? activeSubtab.value - 2 : 0
				else return
			}
			if (activeSubtab.value !== undefined) changeTabByIndex(activeSubtab.value)
		}
	})
	EventBus.on("showHideDeviceMaterialsSubTab_" + props.tabId, (data) => {
		var index = props.fullTabs.findIndex((tab: any) => tab.tabName === tabsDefinitions.packaging.value)
		if (index < 0) return
		props.fullTabs[index].tabs[1].enabled = data // Device Materials

		// Handle Packaging Materials based on the flag
		if (packagingMaterialsAlwaysEnabled.value) {
			// If flag is true: Packaging Materials is always enabled
			props.fullTabs[index].tabs[3].enabled = true
		} else {
			// If flag is false: Packaging Materials is enabled when Device Materials is disabled (inverse logic)
			props.fullTabs[index].tabs[3].enabled = !data
		}
	})
	EventBus.on("setPackagingMaterialsAlwaysEnabled_" + props.tabId, (data) => {
		packagingMaterialsAlwaysEnabled.value = data
	})
	EventBus.on("checkIfDeviceMaterialNeedToBeDisabled_" + props.tabId, () => {
		checkIfDeviceMaterialNeedToBeDisabled()
	})
	EventBus.on("selectAStep_" + props.tabId, (data) => {
		stepClick(data)
	})
	EventBus.on("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, (data) => {
		enableDisableDeviceMaterialPackMaterialResults()
	})
	EventBus.on("checkIfResultNeedToBeEnabled_" + props.tabId, (assessmentId: any) => {
		checkIfResultNeedToBeEnabled(assessmentId)
	})
	EventBus.on("enableDisableRawMaterialTab_" + props.tabId, (enableDisable) => {
		var index = props.fullTabs.findIndex((tab: any) => tab.tabName === props.tabName)
		for (let i = 1; i < props.fullTabs[index].tabs.length; i++) {
			props.fullTabs[index].tabs[i].enabled = enableDisable
		}
		EventBus.emit("updateAllTabsInformationPerMainTabEv", props.fullTabs[index])
	})
	EventBus.on("highlightStep_" + props.tabId, (step: any) => {
		// Don't update selection while a step is loading to prevent race conditions
		if (isStepLoading.value) return
		actualStep.value = step
		selectedStep.value = step
		let selectedStepInUI = steps.value.find((s: Step) => s.id === step.id)
		if (selectedStepInUI === null) return
		highlightStep(step)
		setTimeout(() => {
			highlightStep(step)
		}, 500)
	})
})

// const checkIfEnableRawAndOthers = () => {
// 	var enableDisable = false

// 	var index = props.fullTabs.findIndex((tab: any) => tab.tabName === props.tabName)
// 	for (let i = 1; i < props.fullTabs[index].tabs.length - 1; i++) props.fullTabs[index].tabs[i].enabled = enableDisable
// }

function highlightStep(step: Step) {
	if (!step) return
	let stepsLength = steps.value.length
	step = step as Step
	for (let x = 0; x < stepsLength; x++) {
		const listItem = document.getElementById("stepsList" + props.tabId + "_" + x.toString()) ?? document.createElement("div")
		if (step.id === steps.value[x].id) {
			listItem.setAttribute("aria-selected", "true")
			listItem.classList.add("p-listbox-option-selected")
		} else {
			listItem.setAttribute("aria-selected", "false")
			listItem.classList.remove("p-listbox-option-selected")
		}
	}
	checkIfResultNeedToBeEnabled(props.assessmentId)
}

function checkIfResultNeedToBeEnabled(assessmentId: string) {
	apiClient.checkIfResultNeedToBeEnabledDistributionPackagingByAssessmentId(assessmentId, props.tabId).then((res) => {
		if (props.tabName === tabsDefinitions.packaging.value) {
			var index = props.fullTabs.findIndex((tab: any) => tab.tabName === tabsDefinitions.packaging.value)
			if (index < 0) return
			// Results and Report tabs are always enabled for Device & Packaging
			if (props.fullTabs[index].tabs[4]) props.fullTabs[index].tabs[4].enabled = true
			if (props.fullTabs[index].tabs[5]) props.fullTabs[index].tabs[5].enabled = true
			//Enable compare tab
			if (props.fullTabs[index].tabs[6]) props.fullTabs[index].tabs[6].enabled = true
		}
		if (props.tabName === tabsDefinitions.distribution.value) {
			var index = props.fullTabs.findIndex((tab: any) => tab.tabName === tabsDefinitions.distribution.value)
			if (index < 0) return
			if (props.fullTabs[index].tabs[4]) props.fullTabs[index].tabs[4].enabled = res
		}
		if (props.tabName !== tabsDefinitions.distribution.value && props.tabName !== tabsDefinitions.packaging.value) {
			var index = props.fullTabs.findIndex((tab: any) => tab.tabName === props.tabName)
			if (index >= 0 && props.fullTabs[index].tabs[4]) props.fullTabs[index].tabs[4].enabled = res
		}
	})
}

function checkIfDeviceMaterialNeedToBeDisabled() {
	apiClient.checkIfDeviceMaterialNeedToBeDisabled(generalInfomationIdReal.value).then((res) => {
		var index = props.fullTabs.findIndex((tab: any) => tab.tabName === tabsDefinitions.packaging.value)
		if (index < 0) return
		props.fullTabs[index].tabs[2].enabled = res
		//props.fullTabs[4].tabs[3].enabled = res
	})
}

function enableDisableDeviceMaterialPackMaterialResults() {
	apiClient.checkIfGeneralInfoIsPresentForDistributionPackagingByAssessmentId(props.assessmentId, props.tabId).then((res) => {
		if (props.tabName === tabsDefinitions.packaging.value) {
			var index = props.fullTabs.findIndex((tab: any) => tab.tabName === tabsDefinitions.packaging.value)
			if (index < 0) return
			//props.fullTabs[index].tabs[1].enabled = res
			if (props.fullTabs[index].tabs[2]) props.fullTabs[index].tabs[2].enabled = res

			// Check the flag: if packagingMaterialsAlwaysEnabled is true, always enable Packaging Materials (index 3)
			if (packagingMaterialsAlwaysEnabled.value) {
				if (props.fullTabs[index].tabs[3]) props.fullTabs[index].tabs[3].enabled = true
			} else {
				// Original behavior: disable when res is false
				if (!res && props.fullTabs[index].tabs[3]) {
					props.fullTabs[index].tabs[3].enabled = res
				}
			}

			// Results tab is always enabled for Device & Packaging
			if (props.fullTabs[index].tabs[4]) props.fullTabs[index].tabs[4].enabled = true
			//props.fullTabs[4].tabs[3].enabled = enable
		}
		if (props.tabName === tabsDefinitions.distribution.value) {
			var index = props.fullTabs.findIndex((tab: any) => tab.tabName === tabsDefinitions.distribution.value)
			if (index < 0) return
			if (props.fullTabs[index].tabs[1]) props.fullTabs[index].tabs[1].enabled = res
			if (props.fullTabs[index].tabs[2]) props.fullTabs[index].tabs[2].enabled = res
			if (props.fullTabs[index].tabs[3]) props.fullTabs[index].tabs[3].enabled = res
			if (!res && props.fullTabs[index].tabs[4]) props.fullTabs[index].tabs[4].enabled = res
			//props.fullTabs[5].tabs[4].enabled = enable
		}
		checkIfResultNeedToBeEnabled(props.assessmentId)
	})
}

const setTabInfoGlobally = (type: string, tabId: string, tabContent: any) => {
	switch (type) {
		case "selectedStepId":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).selectedStepId = tabContent
			break
		case "generalInformationId":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).generalInformationId = tabContent
			break
		case "actualSubTabName":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).actualSubTabName = tabContent
			break
	}
	emit("updateAllTabsInformation", props.allTabsInformation)
}

function change(ev: any) {
	if (ev === null) selectedStep.value = actualStep.value
	setTabInfoGlobally("selectedStepId", props.tabId, actualStep.value.id)
}

function getSteps(screenId: string, GI: string) {
	if (GI === null) return
	steps.value = []
	getAllSteps(props.assessmentId, screenId, GI)
	// apiClient.getStepByAssessmentIdScreenIdGeneralInfomationId(props.assessmentId, screenId, GI).then(
	// 	(res) => {
	// 		if (res && res != null && res.length > 0) {
	// 			steps.value = res
	// 		}
	// 	},
	// 	(err) => {
	// 		//toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
	// 	}
	// )
}

async function changeTab(index: number) {
	let clickedSubTableName = tabs.value[index].tabName.replace(/\s+/g, "")
	selectedMainTab.value = index
	activeSubtab.value = index
	localStorage.setItem("actualTab", tabs.value[index].tabName.replace(/\s+/g, ""))

	// Check if Utilities tab is selected and get project info if needed
	if (clickedSubTableName === "Utilities" && !projectInfo.value) {
		try {
			projectInfo.value = await apiClient.getAssessmentWithProject(props.assessmentId)
		} catch (error) {
			console.error("Error fetching project info:", error)
		}
	}

	// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
	// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
	// toUpdate.actualSubTabName = clickedSubTableName ? clickedSubTableName : ""
	// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
	setTabInfoGlobally("actualSubTabName", tabId.value, clickedSubTableName)
	// 	"changeTab",
	// 	props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
	// )
	updateSubTabContent(
		clickedSubTableName,
		props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
	)
	if ((clickedSubTableName == "Result" || clickedSubTableName == "Compare" || clickedSubTableName == "Report") && props.allTabsInformation.length > 0) {
		EventBus.emit("resetResultStatus_" + props.tabId)
		EventBus.emit(
			"validateResult_" + props.tabId,
			props.allTabsInformation.find((item: any) => item.actualMainTabId === tabId.value)
		)
	}
}

function changeTabByIndex(index: any) {
	let clickedSubTableName = tabs.value[index].tabName.replace(/\s+/g, "")
	selectedMainTab.value = index
	localStorage.setItem("actualTab", tabs.value[index].tabName.replace(/\s+/g, ""))
	// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
	// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
	// toUpdate.actualSubTabName = clickedSubTableName ? clickedSubTableName : ""
	// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
	setTabInfoGlobally("actualSubTabName", tabId.value, clickedSubTableName)
	// 	"changeTabByIndex",
	// 	props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
	// )
	updateSubTabContent(
		clickedSubTableName,
		props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
	)
	if (clickedSubTableName == "Result" || clickedSubTableName == "Compare" || clickedSubTableName == "Report") {
		EventBus.emit("resetResultStatus_" + props.tabId)
		EventBus.emit(
			"validateResult_" + props.tabId,
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId.value)
		)
	}
}

function updateSubTabContent(tabName: String, toUpdate?: any) {
	switch (tabName) {
		case "DeviceMaterials":
			//tabName = "RawMaterials"
			break
		case "PackagingMaterials":
			//tabName = "RawMaterials"
			break
		case "DownstreamPackagingMaterials":
			//tabName = "RawMaterials"
			break
		case "GeneralInformation":
			tabName = "GeneralInformation"
			break
		case "PackagingEndOfLife":
			tabName = "ProcessWaste"
			break
	}
	EventBus.emit(tabName + "Update" + tabId.value, {})
	if (tabName == "Result" || tabName == "Compare" || tabName == "Report") {
		EventBus.emit("resetResultStatus_" + props.tabId)
		EventBus.emit("validateResult_" + props.tabId, toUpdate)
	}
}

function toggleSteps() {
	isCollapsed.value = !isCollapsed.value
	let id = "stepsList" + props.tabId
	setListToggleDisplayNone(id)
}

function changeSubTable(tabIndex: number): Promise<string> {
	return new Promise((resolve, reject) => {
		changeTabByIndex(tabIndex)
		activeSubtab.value = tabIndex
		resolve("Done")
	})
}

function getActivePanels() {
	let selectedPanels = document.querySelectorAll(
		"#main-content div.p-tabview-nav-container li.p-tabview-header.p-highlight span.ml-2:not([class*=' ']"
	)
	let len = selectedPanels.length
	let pName = []
	for (let i = 0; i < len; i++) {
		var item = selectedPanels?.item(i)
		let cnt = 0
		let dspNone = false
		while (item.tagName != "HTML") {
			item = item.parentElement ?? document.createElement("HTML")
			if (item.computedStyleMap().get("display")?.toString() === "none") {
				dspNone = true
				break
			}
			if (cnt > 1000) {
				dspNone = true
				break
			}

			cnt++
		}
		if (item.tagName === "HTML" && dspNone === false) {
			let con = selectedPanels.item(i).textContent
			pName.push(con)
		}
	}
	return pName
}

function stepGraphNodeClick(node: Node) {
	const timeSinceLastClick = Date.now() - lastStepClickTime

	// Block clicks while a step is loading OR if clicked too recently (debounce)
	if (isStepLoading.value || timeSinceLastClick < STEP_CLICK_DEBOUNCE_MS) {
		return
	}

	if (node.id) {
		let step = steps.value.find((s: Step) => s.id === node.id)
		if (step) {
			stepClick(step)
		}
	}
}
function stepGraphExpandClick() {
	// Open modal to show bigger graph view
	showExpandedStepsFlow.value = true
}
function graphModalAfterEnter() {
	// We rerender the graph after the modal is shown and transition is completed
	expandedGraphKey.value += 1
}

function stepClick(event: any) {
	const now = Date.now()
	const timeSinceLastClick = now - lastStepClickTime

	// Block clicks while a step is loading OR if clicked too recently (debounce)
	if (isStepLoading.value || timeSinceLastClick < STEP_CLICK_DEBOUNCE_MS) {
		return
	}

	lastStepClickTime = now
	isStepLoading.value = true

	// Store reference to the step being loaded - don't update actualStep until loading completes
	const loadingStep = event

	apiClient
		.rawMaterialExist(event.id)
		.then((rawMatExist) => {
			props.fullTabs[props.selectedMainTabIndex].tabs.forEach((element: any, index: number) => {
				if (index !== 0) element.enabled = false
				if (rawMatExist) {
					// if no raw material
					if (index > 0) {
						element.enabled = true
					}
					//var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
					var actualTab = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId == props.tabId)
					let allActivePanels = getActivePanels()

					let ind = allActivePanels.findIndex((item) => item === "Raw Materials")

					if ((actualTab?.actualSubTabName !== "RawMaterials" || ind === -1) && index > 0) {
						if (event.id == undefined) {
							element.enabled = false
						} else {
							changeSubTable(1).then((res) => {
								if (index === 1) {
									element.enabled = true
								}
							})
						}
					}
				} else {
					//var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
					var actualTab = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId == props.tabId)

					if (actualTab?.actualSubTabName !== "RawMaterials" && index > 0) {
						if (event.id == undefined) {
							element.enabled = false
						} else {
							changeSubTable(1).then((res) => {
								if (index === 1) {
									element.enabled = true
								}
							})
						}
					}
				}
			})
		})
		.then(() => {
			// Update actualStep now that loading is complete
			actualStep.value = loadingStep

			isCollapsed.value = false
			let id = "stepsList" + props.tabId
			setTimeout(() => {
				setListToggleDisplayNone(id, "true")
				highlightStep(loadingStep)
			}, 1000)

			// Update global state and emit events AFTER loading completes
			// This ensures the graph selection and data display are synchronized
			setTabInfoGlobally("selectedStepId", props.tabId, loadingStep.id)
			EventBus.emit("selectedStep_" + props.tabId, loadingStep)

			// Emit update event to load the step data
			updateSubTabContent(props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId == props.tabId).actualSubTabName)

			// Update selectedStep AFTER loading completes - this triggers graph update to show selection
			selectedStep.value = loadingStep

			// Enable Next Prev buttons after loading completes
			EventBus.emit("nextPrevEnabled_" + props.tabId)
		})
		.finally(() => {
			// Delay re-enabling step clicks to allow child components to finish loading
			setTimeout(() => {
				isStepLoading.value = false
			}, STEP_CLICK_DEBOUNCE_MS)
		})
}

function addStep() {
	changeSubTable(0).then((res) => {
		EventBus.emit("addStep_" + generalInfomationIdReal.value)
	})
}

function removeStep(step: Step) {
	apiClient.step(step.id).then(
		(result) => {
			getAllSteps(step.assessmentId, step.screenId, step.generalInformationId)
			EventBus.emit("versionChange", step.generalInformationId)
		},
		(err) => {
			throw err
		}
	)
}

function getAllSteps(assessmentId: string, screenId: string, generalInformationId: string) {
	stepsMessage.value = "Loading..."
	setTimeout(() => {
		apiClient
			.getStepsAndIntermediates(assessmentId, screenId, generalInformationId)
			.then(
				(res) => {
					if (res && res != null && (res.steps?.length ?? 0) > 0) {
						steps.value = res.steps ?? []
						intermediates.value = res.intermediateSteps ?? []
						materialEntryCount.value = res.materialEntryCount ?? {}
					} else {
						stepsMessage.value = "No steps found"
					}
				},
				(err) => {
					stepsMessage.value = `Error: ${err}`
				}
			)
			.then(() => {
				if (selectedStep.value) highlightStep(selectedStep.value)
			})
	}, 300)
}
function shouldHideListView(): boolean {
	// Get the current active subtab name
	const currentSubtabIndex = activeSubtab.value
	const currentSubtabName = tabs.value && tabs.value[currentSubtabIndex] ? tabs.value[currentSubtabIndex].tabName : ""

	// Check if current subtab is "Utilities" and isSingleUtilitiesChecked is true
	if (
		(currentSubtabName === "Utilities" && projectInfo.value?.subProject?.project?.isSingleUtilitiesChecked) ||
		currentSubtabName === "Result" ||
		currentSubtabName === "Results" ||
		currentSubtabName === "Report"
	) {
		return true
	}

	return false
}

function setListToggleDisplayNone(elemId: string, displayNone: string = "toggle") {
	let myRet = window.document.getElementById(elemId)
	// the item should have these classes ".p-listbox .p-component"
	//let myRet = window.document.getElementsByClassName('p-listbox')
	switch (displayNone) {
		case "toggle":
			if (myRet && myRet.classList !== undefined) {
				if (myRet.classList.contains("noneDisplay")) myRet.classList.remove("noneDisplay")
				else myRet.classList.add("noneDisplay")
			}
			break
		case "true":
			if (myRet && myRet.classList !== undefined) if (!myRet.classList.contains("noneDisplay")) myRet.classList.add("noneDisplay")
			break
		case "false":
			if (myRet && myRet.classList !== undefined) if (myRet.classList.contains("noneDisplay")) myRet.classList.remove("noneDisplay")
			break
		default:
			if (myRet && myRet.classList !== undefined) {
				if (myRet.classList.contains("noneDisplay")) myRet.classList.remove("noneDisplay")
				else myRet.classList.add("noneDisplay")
			}
			break
	}
}
</script>
<style scoped>
/* .container {
	display: flex;
	max-width: 100%;
}

.tab-view {
	flex: 1 1 80%;
	max-width: 100%;
}

.list-view {
	color: black;
	background-color: white;
	flex: 0 0 20%; 
	max-width: 20%; 
}

.noneDisplay {
	display: none;
} */

.flex-container {
	display: flex;
	flex-grow: 1;
	width: 100%;
	max-width: 100%;
	overflow: hidden;
	box-sizing: border-box;
}

.tab-view {
	transition: width 0.3s ease;
	min-width: 0;
	overflow: hidden;
	flex-shrink: 1;
	box-sizing: border-box;
}

.w-80 {
	width: 80%;
}

.w-70 {
	width: 70%;
}

.w-100 {
	width: 95%;
}

.w-95 {
	width: 95%;
}

.w-20 {
	width: 20%;
}

.w-30 {
	width: 30%;
}

.w-15 {
	width: 15%;
}

.w-85 {
	width: 85%;
}

.list-view {
	transition: width 0.3s ease;
	flex-shrink: 0;
	overflow: hidden;
	box-sizing: border-box;
}

.w-5 {
	width: 5%;
}

/* General Information Options Listbox Styles */
.general-info-listbox-container {
	background: var(--lca-background-color, #fff);
	border: 1px solid var(--p-surface-200, #e5e7eb);
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.general-info-list {
	list-style: none;
	margin: 0;
	padding: 0;
	max-height: 300px;
	overflow-y: auto;
}

.general-info-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.875rem 1rem;
	cursor: pointer;
	transition: all 0.2s ease;
	border-bottom: 1px solid var(--p-surface-100, #f3f4f6);
	background: transparent;
}

.general-info-item:last-child {
	border-bottom: none;
}

.general-info-item:hover {
	background: var(--p-surface-50, #f9fafb);
}

.general-info-item .item-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex: 1;
	min-width: 0;
}

.general-info-item .item-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	flex-shrink: 0;
}

.general-info-item .item-icon .icon {
	font-size: 1rem;
	color: var(--p-surface-400, #9ca3af);
	transition: all 0.2s ease;
}

.general-info-item .item-text-container {
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
	min-width: 0;
	flex: 1;
}

.general-info-item .item-text {
	font-size: 0.9rem;
	font-weight: 500;
	color: var(--p-surface-700, #374151);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.general-info-item .item-timestamp {
	font-size: 0.75rem;
	color: var(--p-surface-500, #6b7280);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.general-info-item .selected-indicator {
	color: var(--lca-background-color, #fff);
	font-size: 0.75rem;
	opacity: 0.8;
	margin-left: 0.5rem;
}

/* Selected state */
.general-info-item.selected {
	background: linear-gradient(135deg, var(--lca-accent-color, #c99733) 0%, var(--lca-accent-color-dark, #bd8a24) 100%);
	border-bottom-color: rgba(255, 255, 255, 0.1);
}

.general-info-item.selected .item-icon .icon {
	color: var(--lca-background-color, #fff);
}

.general-info-item.selected .item-text {
	color: var(--lca-background-color, #fff);
	font-weight: 600;
}

.general-info-item.selected .item-timestamp {
	color: rgba(255, 255, 255, 0.8);
}

.general-info-item.selected:hover {
	background: linear-gradient(135deg, var(--lca-accent-color-dark, #d49a2a) 0%, var(--lca-accent-color, #e6ad3b) 100%);
}

/* Disabled/Loading state */
.general-info-listbox-container.is-loading {
	pointer-events: none;
	opacity: 0.85;
}

.general-info-item.disabled {
	opacity: 0.5;
	cursor: not-allowed;
	pointer-events: none;
}

.general-info-item .item-icon .icon.spinning {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

/* Scrollbar styling */
.general-info-list::-webkit-scrollbar {
	width: 6px;
}

.general-info-list::-webkit-scrollbar-track {
	background: var(--p-surface-100, #f3f4f6);
}

.general-info-list::-webkit-scrollbar-thumb {
	background: var(--p-surface-300, #d1d5db);
	border-radius: 3px;
}

.general-info-list::-webkit-scrollbar-thumb:hover {
	background: var(--p-surface-400, #9ca3af);
}

/* Position hint banner */
.position-hint-banner {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 4px 8px;
	background-color: #eff6ff;
	border: 1px solid #bfdbfe;
	border-radius: 6px;
	margin-bottom: 4px;
	flex-wrap: wrap;
}

.position-save-btn {
	padding: 2px 8px !important;
	font-size: 0.75rem !important;
	min-height: unset !important;
	height: 24px !important;
}
</style>

<style>
/* Non-scoped: VueFlow renders nodes outside scoped CSS boundary */
.vue-flow__node.step-node-highlighted {
	z-index: 10 !important;
}
.step-node-highlighted .border {
	border-width: 3px !important;
	border-color: #000000 !important;
}
</style>
